import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, type ChatQuota, type Vehicle } from './api';
import { canSend as canSendHelper } from './chat-view';
import {
  useChatHistory,
  useChatQuota,
  useSendChatMessage,
  type ThreadMessage,
} from './chat-hooks';

const SEND_ERROR = "Couldn't send — try again";
/** DOM id of the composer textarea, used to focus it when a prompt is picked. */
const COMPOSER_ID = 'chat-composer';

/** Everything the VehicleChat page renders — derived once so the component stays flat. */
export interface ChatViewState {
  notFound: boolean;
  loading: boolean;
  vehicleLabel: string;
  quota: ChatQuota | undefined;
  messages: ThreadMessage[];
  input: string;
  errorMessage: string | null;
  exhausted: boolean;
  composerDisabled: boolean;
  canSend: boolean;
  setInput: (text: string) => void;
  fillComposer: (text: string) => void;
  handleSend: () => void;
}

function is404(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

function vehicleLabel(vehicle: Vehicle | undefined): string {
  if (!vehicle) return '';
  return vehicle.nickname ?? `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
}

/**
 * Owns the chat page's server reads (vehicle/history/quota), the composer state,
 * and the send flow, exposing flat derived values so `VehicleChat` is pure JSX.
 */
export function useChatViewState(id: string): ChatViewState {
  const vehicleQuery = useQuery({
    queryKey: ['vehicle', id],
    queryFn: () => api.getVehicle(id),
    enabled: Boolean(id),
    retry: false,
  });
  const historyQuery = useChatHistory(id);
  const quotaQuery = useChatQuota(id);
  const sendMut = useSendChatMessage(id);

  const [input, setInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const quota = quotaQuery.data;
  const messages = historyQuery.data ?? [];
  const exhausted = quota !== undefined && quota.remaining <= 0;
  const inFlight = sendMut.isPending;
  // canSendHelper already returns false when the quota is spent, so `exhausted`
  // needs no separate term here.
  const canSend = quota !== undefined && canSendHelper(input, quota, inFlight);

  function fillComposer(text: string): void {
    setInput(text);
    document.getElementById(COMPOSER_ID)?.focus();
  }

  function handleSend(): void {
    if (!canSend) return;
    const text = input;
    setErrorMessage(null);
    setInput('');
    sendMut.mutate(text, {
      onError: (err) => {
        // 403 => hook flips the meter to 0 and the composer disables; everything
        // else (5xx, network, parse errors) restores the text for a retry.
        if (err instanceof ApiError && err.status === 403) return;
        setInput(text);
        setErrorMessage(SEND_ERROR);
      },
    });
  }

  return {
    notFound: is404(vehicleQuery.error) || is404(quotaQuery.error),
    loading: vehicleQuery.isLoading,
    vehicleLabel: vehicleLabel(vehicleQuery.data),
    quota,
    messages,
    input,
    errorMessage,
    exhausted,
    composerDisabled: exhausted || inFlight,
    canSend,
    setInput,
    fillComposer,
    handleSend,
  };
}
